import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Trans, useTranslation } from 'react-i18next';

import { CloseIcon } from '../CloseIcon';
import { useIdentityVerification } from '../../../hooks/useIdentityVerification';
import { OffMarketToastMessage } from '../../utils/types';
import './OffMarketToast.scss';

/**
 * Renders the outcome of an off-market intent action (limit reached, already
 * selling here, matched with the other side) as an in-app toast.
 *
 * The `OffMarketToastMessage` type and its `building.offMarket.toast.*` copy
 * already existed, but nothing ever rendered them — every call site fell back
 * to `window.alert`, which shows the browser's "jaakko.local:3000 says" chrome.
 */

const MESSAGE_KEYS: Record<OffMarketToastMessage, string> = {
  COULD_SELL_NO_BUYERS: 'couldSellNoBuyers',
  COULD_SELL_AND_BUYERS: 'couldSellAndBuyers',
  COULD_BUY_NO_SELLERS: 'couldBuyNoSellers',
  COULD_BUY_AND_SELLERS: 'couldBuyAndSellers',
  USER_SELLER_AND_BUYER: 'userSellerAndBuyer',
  COULD_BUY_LIMIT_REACHED: 'couldBuyLimitReached',
};

const AUTO_DISMISS_MS = 2500;
// Matches the fade-out keyframes; the toast starts fading this far before the
// dismiss so the whole appearance still lasts AUTO_DISMISS_MS.
const FADE_OUT_MS = 220;

type OffMarketToastProps = {
  /**
   * A known outcome code, or already-resolved text for cases with no code of
   * their own (a generic request failure). Anything not in MESSAGE_KEYS is
   * rendered verbatim.
   */
  message: OffMarketToastMessage | string | null;
  onDismiss: () => void;
  /** 'top' over a chat, whose composer owns the bottom edge (a bottom toast covers Send). */
  placement?: 'top' | 'bottom';
};

/** Shows one outcome message, portalled to <body>, and dismisses itself after AUTO_DISMISS_MS. */
const OffMarketToast: React.FC<OffMarketToastProps> = ({ message, onDismiss, placement = 'bottom' }) => {
  const { t } = useTranslation();
  // `false` so the toast never polls verification status — only startVerification
  // is used, and it works without the status fetch.
  const { startVerification } = useIdentityVerification(false);

  const [isLeaving, setIsLeaving] = useState(false);

  // Callers pass `onDismiss` as an inline arrow, so its identity changes on
  // every parent render. Read through a ref and key the timers on the message
  // alone: with it in the dependency list a busy parent restarts the countdown
  // on each render, and the toast outlives AUTO_DISMISS_MS by however long the
  // re-renders keep coming.
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;

  useEffect(() => {
    if (!message) return undefined;
    setIsLeaving(false);
    const fade = setTimeout(() => setIsLeaving(true), AUTO_DISMISS_MS - FADE_OUT_MS);
    const done = setTimeout(() => onDismissRef.current(), AUTO_DISMISS_MS);
    return () => {
      clearTimeout(fade);
      clearTimeout(done);
    };
  }, [message]);

  if (!message) return null;

  const key = MESSAGE_KEYS[message as OffMarketToastMessage];

  // Portalled to <body> because callers render it inside pages that full-screen
  // overlays cover — the Add-my-place wizard portals itself to <body> at a
  // higher layer, so a toast left in the page tree would report a publish
  // failure behind the very wizard that raised it.
  return createPortal(
    <div
      className={`off-market-toast${placement === 'top' ? ' off-market-toast--top' : ''}${isLeaving ? ' off-market-toast--leaving' : ''}`}
      role="status"
      aria-live="polite"
    >
      <p className="off-market-toast__text">
        {message === 'COULD_BUY_LIMIT_REACHED' ? (
          <Trans i18nKey="building.offMarket.toast.couldBuyLimitReached">
            Buy-interest limit reached.
            <button
              type="button"
              className="off-market-toast__link"
              onClick={startVerification}
            >
              Verify your identity
            </button>
            for unlimited proposals.
          </Trans>
        ) : (
          key ? t(`building.offMarket.toast.${key}`) : message
        )}
      </p>
      <button
        type="button"
        className="off-market-toast__close"
        aria-label={t('common.close', 'Close')}
        onClick={onDismiss}
      >
        <CloseIcon size={14} color="#ffffff" />
      </button>
    </div>,
    document.body,
  );
};

export default OffMarketToast;
